import { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';

import useQuery from '../../hooks/useQuery';
import permissions from '../../Apollo/permissions';
import isAllowedToReadAuditLogDebugData from '../isAllowedToReadAuditLogDebugData';
import { auditLogPageQuery, buildVariables } from './audit-log-page-query-builder';
import { normalizeAuditRow } from './normalize-audit-row';
import { deriveQueryStatus } from './query-status';
import useRecordScope from './useRecordScope';
import { DEFAULT_FILTERS, buildFilterWhere, isFilterActive } from './audit-log-filters';
import { buildGlobalFilterWhere, isGlobalFilterActive } from './global-filters';
import { DEFAULT_SORT_DIRECTION } from './paging/sort-direction';
import usePagePosition from './paging/usePagePosition';

export const PAGE_SIZE = 25;

export default function useAuditLogPage({
  recordRef,
  globalFilters,
  filters = DEFAULT_FILTERS,
  pagingStrategy,
  pageSize = PAGE_SIZE,
}) {
  invariant(
    Boolean(recordRef) !== Boolean(globalFilters),
    'useAuditLogPage requires exactly one of recordRef or globalFilters'
  );

  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const scopeKey = recordRef ? `${recordRef.entity}:${recordRef.scopeValues.id}` : 'global';
  const [position, setPosition] = usePagePosition({
    resetKey: `${scopeKey}|${sortDirection}`,
    filters: recordRef ? filters : globalFilters,
    initialPosition: pagingStrategy.initialPosition,
  });

  const scope = useRecordScope({ recordRef, filters });
  const scopePending = Boolean(scope.loading || scope.error);
  const filterWhere = recordRef ? buildFilterWhere(filters) : buildGlobalFilterWhere(globalFilters);
  const pageVariables = pagingStrategy.buildPageVariables({
    scopeWhere: scope.where,
    filterWhere,
    pageSize,
    position,
    direction: sortDirection,
  });
  const variables = buildVariables(pageVariables);

  const includeDebugData = isAllowedToReadAuditLogDebugData();
  const page = useQuery(auditLogPageQuery({ includeDebugData }), {
    variables,
    skip: scopePending,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
    context: { permission: permissionFor({ namesDebugData: includeDebugData || Boolean(recordRef) }) },
  });

  const loading = Boolean(scope.loading || page.loading);
  const fetchedRows = useMemo(() => page.data?.org_audit_logs ?? [], [page.data]);
  const rows = useMemo(() => fetchedRows.slice(0, pageSize).map(normalizeAuditRow), [fetchedRows, pageSize]);

  const { hasNextPage, nextPosition, prevPosition } = pagingStrategy.readPageInfo({
    rows,
    fetchedCount: fetchedRows.length,
    pageSize,
    position,
  });

  const totalCountDoc = pagingStrategy.totalCountDocument;
  const {
    data: totalCountData,
    loading: totalCountLoading,
    error: totalCountError,
  } = useQuery(totalCountDoc ?? auditLogPageQuery({ includeDebugData: false }), {
    skip: !totalCountDoc || scopePending,
    variables: { where: { _and: [scope.where, filterWhere] } },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
    context: { permission: permissionFor({ namesDebugData: Boolean(recordRef) }) },
  });
  const totalCount = totalCountDoc ? totalCountData?.org_audit_logs_aggregate?.aggregate?.totalCount ?? 0 : null;
  const error = scope.error ?? page.error ?? totalCountError;

  return {
    rows,
    pageInfo: {
      hasNextPage,
      hasPrevPage: prevPosition !== null,
      goToNextPage: () => nextPosition && setPosition(nextPosition),
      goToPreviousPage: () => prevPosition && setPosition(prevPosition),
      goToPosition: newPosition => setPosition(newPosition),
    },
    totalCount,
    hasActiveFilters: recordRef ? isFilterActive(filters) : isGlobalFilterActive(globalFilters),
    sortDirection,
    onSortDirectionChange: setSortDirection,
    loading,
    isAllowed: page.isAllowed,
    error,
    status: deriveQueryStatus({ loading: loading || totalCountLoading, error, isEmpty: rows.length === 0 }),
  };
}

// read:audit_logs can resolve to Read-Only, which Hasura denies debug_data, for a member whose roles
// stack Read-Only under a higher one; only roles that can read the column hold the debug-data
// permission. The global page is the one surface Read-Only reaches, so it cannot always use it.
function permissionFor({ namesDebugData }) {
  return namesDebugData ? permissions.readAuditLogDebugData : permissions.readAuditLogs;
}
